/**
 * In-game panels: bag, quest log, NPC dialogue, shop and chat.
 *
 * All built with real DOM rather than drawn into the canvas. Text stays crisp
 * at any render scale, it costs no draw calls, and a screen reader can find it.
 */

import type {
  BagEntry, ItemDef, QuestView, ShopView, TurnInResult
} from '../api/gameTypes.js';
import { RARITY_COLOUR } from '../api/gameTypes.js';
import { api, ApiError } from '../api/client.js';

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing element #${id}`);
  return node as T;
}
const show = (n: HTMLElement): void => n.classList.remove('hidden');
const hide = (n: HTMLElement): void => n.classList.add('hidden');

/** Toast for kills, loot, level-ups and rejections. */
export function toast(message: string, kind: 'good' | 'bad' | 'plain' = 'plain'): void {
  const layer = el('toasts');
  const node = document.createElement('div');
  node.className = `toast ${kind}`;
  node.textContent = message;
  layer.append(node);
  // Removed rather than hidden, so a long session does not accumulate hundreds
  // of dead nodes behind the HUD.
  window.setTimeout(() => node.remove(), 4200);
}

const EQUIP_SLOTS = ['mainhand', 'offhand', 'head', 'chest', 'legs', 'feet', 'trinket'] as const;

const SLOT_LABEL: Record<string, string> = {
  mainhand: 'Weapon', offhand: 'Off hand', head: 'Head', chest: 'Body',
  legs: 'Legs', feet: 'Feet', trinket: 'Trinket'
};

export class GamePanels {
  private characterId: string;
  private marks = 0;
  private bag: BagEntry[] = [];
  private equipment: Record<string, ItemDef> = {};
  private quests: QuestView[] = [];
  private openShop: ShopView | null = null;
  /** Set while a panel is open, so the game loop can stop taking movement. */
  private openPanel: string | null = null;

  /** Called when something changes that the HUD shows. */
  onVitals: ((v: { health?: number; maxHealth?: number; level?: number }) => void) | null = null;

  constructor(characterId: string) {
    this.characterId = characterId;
    this.wire();
  }

  get anyOpen(): boolean {
    return this.openPanel !== null;
  }

  private wire(): void {
    for (const id of ['bag-close', 'quests-close', 'dialogue-close', 'shop-close']) {
      el(id).addEventListener('click', () => this.closeAll());
    }
    // Escape closes whatever is open before it reaches the settings screen.
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.openPanel !== null) {
        e.stopPropagation();
        this.closeAll();
      }
    }, true);
  }

  closeAll(): void {
    for (const id of ['panel-bag', 'panel-quests', 'panel-dialogue', 'panel-shop']) {
      hide(el(id));
    }
    this.openPanel = null;
  }

  // --- bag -----------------------------------------------------------------

  async openBag(): Promise<void> {
    this.closeAll();
    this.openPanel = 'bag';
    show(el('panel-bag'));
    await this.refreshBag();
  }

  async refreshBag(): Promise<void> {
    try {
      const data = await api.inventory(this.characterId);
      this.bag = data.bag;
      this.equipment = data.equipment;
      this.marks = data.marks;
      this.renderBag(data.stats, data.bagSlots);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not open your bag.', 'bad');
    }
  }

  private renderBag(stats: { attack: number; defence: number }, bagSlots: number): void {
    el('bag-marks').textContent = `${this.marks} Marks`;
    el('bag-stats').textContent = `Attack ${stats.attack} · Defence ${stats.defence}`;

    const worn = el('bag-equipment');
    worn.replaceChildren();
    for (const slot of EQUIP_SLOTS) {
      const item = this.equipment[slot];
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'equip-row' + (item === undefined ? ' empty' : '');
      row.innerHTML = '';

      const label = document.createElement('span');
      label.className = 'equip-label';
      label.textContent = SLOT_LABEL[slot] ?? slot;

      const name = document.createElement('span');
      name.className = 'equip-name';
      if (item === undefined) {
        name.textContent = '—';
      } else {
        name.textContent = `${item.icon} ${item.name}`;
        name.style.color = RARITY_COLOUR[item.rarity] ?? '#f4ece0';
      }

      row.append(label, name);
      if (item !== undefined) {
        row.title = `Take off ${item.name}`;
        row.addEventListener('click', () => { void this.unequip(slot); });
      }
      worn.append(row);
    }

    const grid = el('bag-grid');
    grid.replaceChildren();
    const bySlot = new Map(this.bag.map((entry) => [entry.slotIndex, entry]));

    for (let i = 0; i < bagSlots; i++) {
      const entry = bySlot.get(i);
      const cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'bag-cell' + (entry === undefined ? ' empty' : '');

      if (entry !== undefined) {
        cell.innerHTML = '';
        const icon = document.createElement('span');
        icon.className = 'bag-icon';
        icon.textContent = entry.item.icon;
        cell.append(icon);

        if (entry.quantity > 1) {
          const count = document.createElement('span');
          count.className = 'bag-count';
          count.textContent = String(entry.quantity);
          cell.append(count);
        }

        cell.style.borderColor = RARITY_COLOUR[entry.item.rarity] ?? '#4a352a';
        cell.title =
          `${entry.item.name}\n${entry.item.description}` +
          (entry.item.attack > 0 ? `\nAttack +${entry.item.attack}` : '') +
          (entry.item.defence > 0 ? `\nDefence +${entry.item.defence}` : '') +
          (entry.item.heal > 0 ? `\nRestores ${entry.item.heal} health` : '') +
          (entry.item.bound ? '\nQuest item — cannot be sold' : '');

        cell.addEventListener('click', () => { void this.useOrEquip(entry.item); });
      }
      grid.append(cell);
    }
  }

  private async useOrEquip(item: ItemDef): Promise<void> {
    try {
      if (item.category === 'consumable') {
        const after = await api.useItem(this.characterId, item.key);
        this.onVitals?.({ health: after.health, maxHealth: after.maxHealth });
        toast(`Used ${item.name}`, 'good');
      } else if (item.slot !== null) {
        await api.equip(this.characterId, item.key);
        toast(`Equipped ${item.name}`, 'good');
      } else {
        toast(`${item.name} — ${item.description}`);
        return;
      }
      await this.refreshBag();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'That did not work.', 'bad');
    }
  }

  private async unequip(slot: string): Promise<void> {
    try {
      await api.unequip(this.characterId, slot);
      await this.refreshBag();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'That did not work.', 'bad');
    }
  }

  // --- quests --------------------------------------------------------------

  async openQuests(): Promise<void> {
    this.closeAll();
    this.openPanel = 'quests';
    show(el('panel-quests'));
    await this.refreshQuests();
  }

  async refreshQuests(): Promise<void> {
    try {
      this.quests = (await api.quests(this.characterId)).quests;
      this.renderQuests();
      this.renderTracker();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not load your quests.', 'bad');
    }
  }

  private renderQuests(): void {
    const list = el('quest-list');
    list.replaceChildren();

    // Locked quests are deliberately not shown at all. A list of things you
    // cannot do yet is noise, and it spoils the chain.
    const visible = this.quests.filter((q) => q.state !== 'locked');
    if (visible.length === 0) {
      const note = document.createElement('p');
      note.className = 'empty-note';
      note.textContent = 'Nothing on. Talk to somebody in town.';
      list.append(note);
      return;
    }

    const order: QuestView['state'][] = ['ready', 'active', 'available', 'complete'];
    visible.sort((a, b) => order.indexOf(a.state) - order.indexOf(b.state));

    for (const quest of visible) {
      const card = document.createElement('div');
      card.className = `quest-card ${quest.state}`;

      const head = document.createElement('div');
      head.className = 'quest-head';
      const name = document.createElement('strong');
      name.textContent = quest.name;
      const badge = document.createElement('span');
      badge.className = 'quest-badge';
      badge.textContent = {
        ready: 'Ready to hand in', active: 'In progress',
        available: 'Available', complete: 'Done', locked: ''
      }[quest.state];
      head.append(name, badge);

      const summary = document.createElement('p');
      summary.className = 'tiny muted';
      summary.textContent = quest.summary;

      card.append(head, summary);

      if (quest.state === 'active' || quest.state === 'ready') {
        const objectives = document.createElement('ul');
        objectives.className = 'objectives';
        for (const objective of quest.objectives) {
          const line = document.createElement('li');
          line.className = objective.done ? 'done' : '';
          line.textContent = objective.required > 1
            ? `${objective.description} — ${objective.counted}/${objective.required}`
            : objective.description;
          objectives.append(line);
        }
        card.append(objectives);
      }

      if (quest.rewardMarks > 0 || quest.rewardExperience > 0) {
        const reward = document.createElement('p');
        reward.className = 'tiny reward';
        reward.textContent =
          `Reward: ${quest.rewardExperience} xp` +
          (quest.rewardMarks > 0 ? ` · ${quest.rewardMarks} Marks` : '');
        card.append(reward);
      }

      list.append(card);
    }
  }

  /** The two-line tracker on the HUD, showing whatever is closest to done. */
  private renderTracker(): void {
    const tracker = el('quest-tracker');
    const active = this.quests.filter((q) => q.state === 'active' || q.state === 'ready');

    if (active.length === 0) { hide(tracker); return; }

    // Ready first, then the one with the most progress — that is the one the
    // player is most likely to be working on right now.
    active.sort((a, b) => {
      if (a.state !== b.state) return a.state === 'ready' ? -1 : 1;
      const progress = (q: QuestView): number =>
        q.objectives.reduce((sum, o) => sum + o.counted / Math.max(1, o.required), 0);
      return progress(b) - progress(a);
    });

    const quest = active[0]!;
    tracker.replaceChildren();

    const title = document.createElement('div');
    title.className = 'tracker-title';
    title.textContent = quest.name;
    tracker.append(title);

    for (const objective of quest.objectives) {
      if (objective.done && quest.state !== 'ready') continue;
      const line = document.createElement('div');
      line.className = 'tracker-line' + (objective.done ? ' done' : '');
      line.textContent = objective.required > 1
        ? `${objective.description} ${objective.counted}/${objective.required}`
        : objective.description;
      tracker.append(line);
    }

    if (quest.state === 'ready') {
      const line = document.createElement('div');
      line.className = 'tracker-line ready';
      line.textContent = 'Ready to hand in';
      tracker.append(line);
    }

    show(tracker);
  }

  // --- dialogue ------------------------------------------------------------

  async talkTo(npcKey: string): Promise<void> {
    try {
      const data = await api.talk(this.characterId, npcKey);
      this.closeAll();
      this.openPanel = 'dialogue';

      el('dialogue-name').textContent = data.npc.name;
      el('dialogue-text').textContent = data.npc.greeting;

      const actions = el('dialogue-actions');
      actions.replaceChildren();

      for (const quest of data.turnIns) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'primary';
        button.textContent = `Hand in: ${quest.name}`;
        button.addEventListener('click', () => { void this.turnIn(quest, npcKey); });
        actions.append(button);
      }

      for (const quest of data.offers) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dialogue-offer';
        button.innerHTML = '';
        const name = document.createElement('strong');
        name.textContent = quest.name;
        const text = document.createElement('span');
        text.textContent = quest.offerText;
        button.append(name, text);
        button.addEventListener('click', () => { void this.accept(quest); });
        actions.append(button);
      }

      for (const quest of data.inProgress) {
        const note = document.createElement('p');
        note.className = 'tiny muted';
        note.textContent = `${quest.name} — not finished yet.`;
        actions.append(note);
      }

      if (data.npc.shopKey !== null) {
        const shopKey = data.npc.shopKey;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dialogue-offer';
        button.textContent = 'Show me what you have';
        button.addEventListener('click', () => { void this.openShopPanel(shopKey); });
        actions.append(button);
      }

      show(el('panel-dialogue'));
      // Talking may have completed a talk objective.
      await this.refreshQuests();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'They did not answer.', 'bad');
    }
  }

  private async accept(quest: QuestView): Promise<void> {
    try {
      await api.acceptQuest(this.characterId, quest.key);
      toast(`Taken on: ${quest.name}`, 'good');
      this.closeAll();
      await this.refreshQuests();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not take that on.', 'bad');
    }
  }

  private async turnIn(quest: QuestView, npcKey: string): Promise<void> {
    try {
      const { result } = await api.turnInQuest(this.characterId, quest.key, npcKey);
      this.showTurnIn(result);
      await Promise.all([this.refreshQuests(), this.refreshBag()]);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not hand that in.', 'bad');
    }
  }

  private showTurnIn(result: TurnInResult): void {
    el('dialogue-text').textContent = result.completeText;
    const actions = el('dialogue-actions');
    actions.replaceChildren();

    const reward = document.createElement('p');
    reward.className = 'reward';
    reward.textContent =
      `+${result.experience} xp` + (result.marks > 0 ? ` · +${result.marks} Marks` : '');
    actions.append(reward);

    if (result.levelledUp) {
      toast(`Level ${result.level}!`, 'good');
      this.onVitals?.({ level: result.level });
    }
  }

  // --- shop ----------------------------------------------------------------

  async openShopPanel(shopKey: string): Promise<void> {
    try {
      const { shop } = await api.shop(shopKey);
      this.openShop = shop;
      this.closeAll();
      this.openPanel = 'shop';
      el('shop-name').textContent = shop.name;
      show(el('panel-shop'));
      await this.refreshBag();
      this.renderShop();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'The shop is shut.', 'bad');
    }
  }

  private renderShop(): void {
    const shop = this.openShop;
    if (shop === null) return;

    el('shop-marks').textContent = `${this.marks} Marks`;

    const buying = el('shop-buy-list');
    buying.replaceChildren();
    for (const listing of shop.listings) {
      const row = document.createElement('div');
      row.className = 'shop-row';

      const name = document.createElement('span');
      name.className = 'shop-item';
      name.textContent = `${listing.icon} ${listing.name}`;
      name.style.color = RARITY_COLOUR[listing.rarity] ?? '#f4ece0';

      const detail = document.createElement('span');
      detail.className = 'tiny muted';
      detail.textContent =
        (listing.attack > 0 ? `atk ${listing.attack} ` : '') +
        (listing.defence > 0 ? `def ${listing.defence} ` : '') +
        (listing.stock !== null ? `· ${listing.stock} left` : '');

      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'shop-action';
      buy.textContent = `Buy ${listing.price}`;
      buy.disabled = listing.stock === 0 || this.marks < listing.price;
      buy.addEventListener('click', () => { void this.buy(listing.itemKey, listing.name); });

      row.append(name, detail, buy);
      buying.append(row);
    }

    const selling = el('shop-sell-list');
    selling.replaceChildren();
    // Bound items are not offered at all — the server refuses them, and a
    // button that always errors is worse than no button.
    const sellable = this.bag.filter((entry) => !entry.item.bound && entry.item.value > 0);

    if (sellable.length === 0) {
      const note = document.createElement('p');
      note.className = 'tiny muted';
      note.textContent = 'Nothing here they want.';
      selling.append(note);
    }

    for (const entry of sellable) {
      const price = Math.floor(entry.item.value * shop.sellMultiplier);
      const row = document.createElement('div');
      row.className = 'shop-row';

      const name = document.createElement('span');
      name.className = 'shop-item';
      name.textContent = `${entry.item.icon} ${entry.item.name}` +
        (entry.quantity > 1 ? ` ×${entry.quantity}` : '');

      const spacer = document.createElement('span');

      const sell = document.createElement('button');
      sell.type = 'button';
      sell.className = 'shop-action';
      sell.textContent = `Sell ${price}`;
      sell.disabled = price <= 0;
      sell.addEventListener('click', () => { void this.sell(entry.item.key, entry.item.name); });

      row.append(name, spacer, sell);
      selling.append(row);
    }
  }

  private async buy(itemKey: string, name: string): Promise<void> {
    if (this.openShop === null) return;
    try {
      const result = await api.buy(this.characterId, this.openShop.key, itemKey, 1);
      this.marks = result.marks;
      toast(`Bought ${name}`, 'good');
      await this.refreshBag();
      this.renderShop();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not buy that.', 'bad');
    }
  }

  private async sell(itemKey: string, name: string): Promise<void> {
    if (this.openShop === null) return;
    try {
      const result = await api.sellItem(this.characterId, this.openShop.key, itemKey, 1);
      this.marks = result.marks;
      toast(`Sold ${name} for ${result.earned}`, 'good');
      await this.refreshBag();
      this.renderShop();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'They will not take that.', 'bad');
    }
  }
}
